import { useEffect, useState } from "react";
import { coffreList, coffreSearch, coffreStatus, type CoffreEntry } from "../api/coffre";
import { IcoClose, IcoFolder } from "./icons";

function isPdf(e: CoffreEntry): boolean {
  return e.kind === "file" && /\.pdf$/i.test(e.name || e.path);
}

export function CoffreBrowseSheet({
  mode,
  title,
  confirm,
  busy = false,
  banner = "",
  onPickFile,
  onPickFolder,
  onClose,
}: {
  mode: "file" | "folder";
  title: string;
  confirm?: string;
  busy?: boolean;
  banner?: string;
  onPickFile?: (entry: CoffreEntry) => void;
  onPickFolder?: (path: string) => void;
  onClose: () => void;
}) {
  const [path, setPath] = useState("");
  const [entries, setEntries] = useState<CoffreEntry[]>([]);
  const [q, setQ] = useState("");
  const [needle, setNeedle] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = window.setTimeout(() => setNeedle(q.trim()), q.trim() ? 220 : 0);
    return () => window.clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setErr("");
    (async () => {
      const st = await coffreStatus();
      if (!live) return;
      if (!st.unlocked) {
        setErr(st.message || "Déverrouillez le Coffre pour continuer.");
        setEntries([]);
        setLoading(false);
        return;
      }
      try {
        const rows =
          mode === "file" && needle.length >= 2
            ? await coffreSearch(needle)
            : await coffreList(path, mode === "folder" ? "picker" : "vault");
        if (!live) return;
        setEntries(rows);
      } catch (e) {
        if (live) setErr(e instanceof Error ? e.message : "Coffre indisponible.");
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [path, needle, mode]);

  const crumbs = path.split("/").filter(Boolean);
  const searching = mode === "file" && needle.length >= 2;
  const folders = entries.filter((e) => e.kind === "folder");
  const files = mode === "file" ? entries.filter(isPdf) : [];

  return (
    <div className="sg-send-back" role="dialog" aria-label={title}>
      <div className="sg-send is-wide">
        <h2>{title}</h2>
        {mode === "file" ? (
          <input
            className="sg-input"
            value={q}
            placeholder="Rechercher un PDF…"
            onChange={(e) => setQ(e.target.value)}
          />
        ) : null}
        {!searching ? (
          <p className="sg-coffre-path">
            <button type="button" className="sg-btn ghost" disabled={busy || path === ""} onClick={() => setPath("")}>
              Coffre
            </button>
            {crumbs.map((c, i) => (
              <button
                key={`${c}-${i}`}
                type="button"
                className="sg-btn ghost"
                disabled={busy}
                onClick={() => setPath(crumbs.slice(0, i + 1).join("/"))}
              >
                {c}
              </button>
            ))}
          </p>
        ) : (
          <p className="sg-legal">Recherche dans tout le Coffre</p>
        )}
        {err || banner ? <p className="sg-err">{banner || err}</p> : null}
        {loading ? <p className="sg-legal">Lecture du Coffre…</p> : null}
        <div className="sg-move-list">
          {folders.map((e) => (
            <button
              key={e.path}
              type="button"
              className="sg-btn ghost"
              disabled={busy}
              onClick={() => {
                setQ("");
                setPath(e.path);
              }}
            >
              <IcoFolder />
              {e.name}
            </button>
          ))}
          {files.map((e) => (
            <button key={e.path} type="button" className="sg-btn ghost is-file" disabled={busy} onClick={() => onPickFile?.(e)}>
              <strong>{e.name}</strong>
              {searching ? <span className="sg-legal">{e.path}</span> : null}
            </button>
          ))}
          {!loading && folders.length === 0 && files.length === 0 && !err ? (
            <p className="sg-legal">{mode === "file" ? "Aucun PDF ici." : "Aucun dossier ici."}</p>
          ) : null}
        </div>
        <div className="sg-send__actions">
          <button type="button" className="sg-btn ghost" disabled={busy} onClick={onClose}>
            <IcoClose />
            Annuler
          </button>
          {mode === "folder" ? (
            <button type="button" className="sg-btn" disabled={busy} onClick={() => onPickFolder?.(path)}>
              {busy ? "Classement…" : confirm || "Classer ici"}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
